'use client'

import * as React from 'react'
import { CompactLabel } from 'app/components/CompactLabel'
import { LockClosedIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Popover as PopoverPrimitive } from 'radix-ui'
import { FilterList, WarningCircle } from 'iconoir-react'
import { compact } from 'lodash'
import { FilterBadge } from 'app/components/shared/styled/FilterBadge'
import { Button } from 'app/components/ui/button'
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from 'app/components/ui/sheet'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { FilterObject, FilterableColumn, FilterValueType } from 'app/filter.utils'
import { closeModal, showModal } from 'app/lib/modal'
import { cn } from 'app/lib/utils'
import type { FixedRules } from 'app/types/filters'
import { CreateNewPage } from 'app/components/table/filters/CreateNewPage'
import LocationFilter from 'app/components/table/filters/LocationFilter'
import ContactOwnerFilter from 'app/components/table/filters/ContactOwnerFilter'
import { FiltersContainerView } from './filters-container'
import { useTableFilters, type TableFiltersProps } from './use-table-filters'

export type { TableFiltersProps }

function FixedRulesView({ source, rules }: FixedRules) {
  return (
    <div data-testid="filter-fixed-rules" className="px-4 py-3">
      <p className="flex items-center gap-1.5 text-label-sm text-default">
        <LockClosedIcon aria-hidden="true" className="size-3.5 text-subtle" strokeWidth={2} />
        {source} always applies
      </p>
      <ul className="mt-2 flex flex-wrap gap-1.5">
        {rules.map((rule) => (
          <li key={rule} className="rounded-md border border-default bg-surface px-2 py-0.5 text-body-sm text-default">
            {rule}
          </li>
        ))}
      </ul>
    </div>
  )
}

const FILTER_COMPONENT_MAP: Record<
  string,
  React.ComponentType<{
    preAppliedValues?: string | string[]
    customSearchParam: string
    defaultFilters?: FilterObject[]
  }>
> = {
  locationId: LocationFilter,
  'leadOwner.name': ContactOwnerFilter,
}

function createStandaloneFilters(
  customSearchParam: string,
  columns?: FilterableColumn[],
  defaultFilters?: FilterableColumn[],
): React.ReactElement[] {
  if (!columns) return []
  const defaultFilterObjects: FilterObject[] =
    defaultFilters?.map((f) => ({
      attribute: f.attribute,
      displayName: f.displayName,
      operator: f.defaultOperator!,
      value: f.defaultValue,
      type: f.type as FilterValueType,
    })) || []

  return compact(
    columns
      .filter((c) => c.standalone)
      .map((column) => {
        const FilterComponent = FILTER_COMPONENT_MAP[column.attribute]
        if (!FilterComponent) return null
        return (
          <FilterComponent
            key={column.attribute}
            preAppliedValues={column.defaultValue}
            customSearchParam={customSearchParam}
            defaultFilters={defaultFilterObjects}
          />
        )
      }),
  )
}

export function TableFiltersView({
  className,
  columns,
  customSearchParam = `filters`,
  fixedRules,
  ...props
}: TableFiltersProps & { fixedRules?: FixedRules }) {
  const {
    isOpen,
    filters,
    join,
    canCreateList,
    pageSupportNewList,
    filteredAppliedTermsCount,
    dropdownFilters,
    defaultFilters,
    handleOpenChange,
    handleFilterChange,
    handleJoinChange,
    applyFilters,
    removeFilter,
    clearFilters,
    setOpen,
  } = useTableFilters({ className, columns, customSearchParam, ...props })

  const isMobile = useIsMobile()
  // Standalone quick filters (Locations, Contact Owner) are redundant on mobile —
  // they're all reachable through the Filters builder — and they crowd the
  // narrow toolbar, so hide them below md.
  const standaloneFilters = isMobile ? [] : createStandaloneFilters(customSearchParam, columns, defaultFilters)
  const builderColumns = columns?.filter((c) => !c.standalone) ?? []
  const appliedCount = filteredAppliedTermsCount

  const filterButton = (
    <Button
      variant="white"
      size="sm"
      aria-label="Filters"
      className={cn(`gap-x-2`, appliedCount > 0 ? `text-primary-600 hover:text-primary-600` : `text-default`)}
    >
      <FilterList className={cn(appliedCount > 0 ? `text-primary-600` : `text-default`)} width={16} height={16} />
      {!isMobile && <CompactLabel className="text-ui-xs" full="Filters" iconOnly />}
      <FilterBadge count={appliedCount} />
    </Button>
  )

  const filtersBody = (
    <>
      {fixedRules && <FixedRulesView {...fixedRules} />}
      <FiltersContainerView
        columns={builderColumns}
        appliedFilters={dropdownFilters}
        useOrOption={props.useOrOption}
        defaultJoin={join}
        onFilterChange={handleFilterChange}
        onJoinChange={handleJoinChange}
        onRemove={removeFilter}
      />
    </>
  )

  const footerControls = (
    <div className="flex items-center justify-between p-4 align-middle">
      <div className="flex items-center justify-between space-x-2 align-middle">
        {pageSupportNewList && canCreateList && (
          <>
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                showModal(
                  <CreateNewPage filters={[...filters]} join={join} onSuccess={closeModal} onCancel={closeModal} />,
                  { title: `Create new list`, isFullScreen: false, useRadix: true },
                )
              }}
            >
              Create new list
            </Button>
            <TooltipWrapper message="Create a list based on the currently selected filters.">
              <WarningCircle width={20} height={20} className="rotate-180 cursor-help text-strong" />
            </TooltipWrapper>
          </>
        )}
      </div>
      <div className="flex items-center justify-between space-x-2 align-middle">
        <Button variant="white" size="md" onClick={clearFilters}>
          Clear All
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={() => {
            applyFilters()
            setOpen(false)
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  )

  // On mobile the desktop popover (min-w-182 ≈ 728px) overflows the screen, so
  // the filter builder opens as a full-width bottom sheet with a scrollable
  // body and pinned footer instead.
  if (isMobile) {
    return (
      <div className={cn(`flex items-start`, className)}>
        <Sheet
          open={isOpen}
          onOpenChange={(open) => {
            handleOpenChange(open)
            if (!open) applyFilters()
          }}
        >
          <SheetTrigger asChild>{filterButton}</SheetTrigger>
          <SheetContent
            side="bottom"
            className="flex max-h-[85dvh] flex-col rounded-t-xl p-0"
            data-testid="filter-builder"
          >
            <SheetHeader className="shrink-0 flex-row items-center justify-between border-b border-default p-4">
              <SheetTitle>Filters</SheetTitle>
              <SheetClose asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Close" className="text-default">
                  <XMarkIcon className="h-5 w-5" />
                </Button>
              </SheetClose>
            </SheetHeader>
            <div className="min-h-0 flex-1 divide-y divide-default overflow-y-auto">
              {builderColumns.length > 0 && filtersBody}
            </div>
            {builderColumns.length > 0 && (
              <div className="shrink-0 border-t border-default pb-[env(safe-area-inset-bottom)]">{footerControls}</div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    )
  }

  return (
    <div className={cn(`flex items-start space-x-2`, className)}>
      <PopoverPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverPrimitive.Trigger asChild>{filterButton}</PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            side="bottom"
            align="start"
            sideOffset={8}
            data-testid="filter-builder"
            className={cn(
              `z-40 flex flex-col divide-y divide-default rounded border border-default bg-surface-raised shadow-elevation-4`,
              builderColumns.length > 0 && `min-w-182`,
            )}
            onInteractOutside={() => {
              applyFilters()
            }}
          >
            {builderColumns.length > 0 && (
              <>
                {filtersBody}
                {footerControls}
              </>
            )}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>

      {standaloneFilters.map((filter, index) => (
        <div key={index}>{filter}</div>
      ))}
    </div>
  )
}
